import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CoachSidebar } from '../../../components/coach/dashboard/CoachSidebar';
import coachStaffService from '../../../services/coachStaffService';
import { AddStaffModal } from './AddStaffModal';

export const StaffSection = () => {
  const { activeView } = useOutletContext();

  const [staff, setStaff] = useState([]);
  const [slotsInfo, setSlotsInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await coachStaffService.getStaff();
      setStaff(data.staff || []);
      setSlotsInfo(data.slotsInfo || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load staff. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // Called after a staff member is successfully added
  const handleStaffAdded = (data) => {
    setShowAddModal(false);
    if (data?.slotsInfo) setSlotsInfo(data.slotsInfo);
    fetchStaff();
  };

  // Only treat slots as "full" when a real limit exists (total > 0) and none remain.
  // total === 0 means no limit is configured yet, so creating is still allowed.
  const noSlotsLeft = !!slotsInfo && slotsInfo.total > 0 && slotsInfo.remaining <= 0;

  const getInitials = (member) =>
    `${(member.firstName || '').charAt(0)}${(member.lastName || '').charAt(0)}`.toUpperCase();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <CoachSidebar activeView={activeView} />
      </div>

      {/* Content */}
      <section className="lg:col-span-3 w-full mx-auto bg-jp-card border border-jp-line rounded-3xl shadow-sm p-4 sm:p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-anton text-jp-ink tracking-wide">STAFF</h2>
            <p className="text-jp-muted text-sm font-roboto mt-1">
              Manage your coaching staff and their access.
            </p>
          </div>

          <div className="flex items-center gap-4">
            {slotsInfo && (
              <div className="text-right">
                <p className="text-xs text-jp-muted font-roboto">Slots used</p>
                <p className="text-lg font-anton text-jp-accent-ink">
                  {slotsInfo.used} <span className="text-jp-muted">/ {slotsInfo.total}</span>
                </p>
              </div>
            )}

            <button
              onClick={() => setShowAddModal(true)}
              disabled={noSlotsLeft}
              title={noSlotsLeft ? 'No staff slots remaining' : 'Add a new staff member'}
              className="px-5 py-3 rounded-xl font-medium font-roboto text-sm transition-all bg-cyan-400 text-slate-900 hover:bg-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + Create Staff
            </button>
          </div>
        </div>

        {/* No slots warning */}
        {noSlotsLeft && (
          <div className="bg-yellow-500/10 border border-yellow-500/40 text-yellow-200 px-4 py-3 rounded-xl text-sm font-roboto mb-6">
            You have used all your staff slots ({slotsInfo.used}/{slotsInfo.total}). Remove a member or upgrade your plan to add more.
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-xl text-sm font-roboto mb-6">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-jp-muted font-roboto">Loading staff...</p>
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-jp-muted font-roboto mb-4">No staff members yet.</p>
            {!noSlotsLeft && (
              <button
                onClick={() => setShowAddModal(true)}
                className="px-5 py-3 rounded-xl font-medium font-roboto text-sm bg-cyan-400 text-slate-900 hover:bg-cyan-300 transition-all"
              >
                + Create your first staff member
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-jp-line text-jp-muted text-xs font-roboto uppercase">
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Job Title</th>
                  <th className="py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr
                    key={member._id}
                    className="border-b border-jp-line hover:bg-jp-line transition-colors"
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-jp-accent/40 flex items-center justify-center text-jp-accent-ink font-semibold text-sm flex-shrink-0">
                          {member.profileImage ? (
                            <img
                              src={member.profileImage}
                              alt={member.firstName}
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            getInitials(member)
                          )}
                        </div>
                        <span className="text-jp-ink font-roboto">
                          {member.firstName} {member.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-jp-muted font-roboto">{member.email}</td>
                    <td className="py-4 px-4 text-jp-muted font-roboto capitalize">
                      {member.jobTitle || '-'}
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-roboto capitalize ${
                          member.registrationStatus === 'approved'
                            ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                            : 'bg-yellow-500/20 text-amber-600 dark:text-yellow-400'
                        }`}
                      >
                        {member.registrationStatus || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Add Staff Modal */}
      {showAddModal && (
        <AddStaffModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleStaffAdded}
        />
      )}
    </div>
  );
};

export default StaffSection;
